import { AfterViewInit, Component, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import {
  GuardsCheckEnd,
  GuardsCheckStart,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterEvent
} from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';
import { OidcSecurityService } from 'angular-auth-oidc-client';

import { AuditScreen, SharedAuditFacade } from '@sd-portal/shared/audit';
import { SdNavService } from '@sd-portal/shared/navigation';
import {
  AppMonitoringService,
  AppUserInfoFacade,
  GoogleAnalyticsTrackingService,
  LoaderService
} from '@sd-portal/shared/shell';
import { RouteStateAnimation, AnalyticsService } from '@sd-portal/shared/util';
import { untilViewDestroyed } from '@sd-portal/shared/rxjs';
import { UserConfigurationFacade } from '@sd-portal/shared/user-configuration';
import { PermissionsService, PolicyType, permissionTypes } from '@sd-portal/shared/permissions';
import { RefreshTokenService } from '@sd-portal/shared/util';
import { SessionStorageKeys } from 'libs/shared/models/src/lib/session-storage-keys.enum';
import { defineUser } from '../sentry';
import { CsatFacade, CsatModalService } from '@sd-portal/shared/csat';

const APPIN_CUSTOMER_ID_KEY =
  'http://schemas.satcomdirect.com/ws/2014/10/identity/claims/customerids';
const { writeAccess } = permissionTypes.preflight.canSendMobileNotificaitons;

// provides Angular app access to ga function
// eslint-disable-next-line @typescript-eslint/ban-types
declare let gtag: Function;

@Component({
  selector: 'sd-portal-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [RouteStateAnimation],
  providers: [AnalyticsService]
})
export class AppComponent implements AfterViewInit, OnInit, OnDestroy {
  enableAudit$: Observable<boolean>;
  auditScreen$: Observable<AuditScreen | null>;
  auditScreens$: Observable<AuditScreen[] | null>;
  selectedAuditScreen$: Observable<AuditScreen | null>;
  canSendNotifications: boolean;

  routeState: string = 'active';
  showSiteMenu: boolean;
  analyticsTrackingID: string;
  resizeListener: () => void;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly swUpdate: SwUpdate,
    private readonly router: Router,
    private readonly sharedAuditFacade: SharedAuditFacade,
    private readonly loaderService: LoaderService,
    readonly navService: SdNavService,
    readonly csatModalService: CsatModalService,
    private readonly auditFacade: SharedAuditFacade,
    private readonly oidcSecurityService: OidcSecurityService,
    private readonly userConfigurationFacade: UserConfigurationFacade,
    private readonly csatFacade: CsatFacade,
    private readonly permissionsService: PermissionsService,
    private readonly renderer: Renderer2,
    private readonly gaTrackingService: GoogleAnalyticsTrackingService,
    private readonly appUserInfoFacade: AppUserInfoFacade,
    private readonly appMonitoringService: AppMonitoringService,
    private readonly analyticsService: AnalyticsService,
    private readonly refreshTokenService: RefreshTokenService
  ) {
  }

  private initPermissions() {
    this.permissionsService
      .currentUserCan(writeAccess)
      .subscribe(val => (this.canSendNotifications = val));
  }

  /* life-cycle */
  ngOnInit() {
    this.resizeListener = () => {
      this.analyticsService.sendEvent('zoom', {
        zoom1: Math.ceil(window.devicePixelRatio * 100 / 2),
        zoom2:  Math.ceil(((window.outerWidth - 10) / window.innerWidth) * 100)
      })
    };
    window.addEventListener('resize', this.resizeListener);

    this.init();

    // Skip permission init for external grant routes (they don't need auth)
    if (!this.router.url.startsWith('/profile/external-grant')) {
      this.initPermissions();
    }

    // needed to allow routes to re-initialize
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;

    this.enableAudit$ = this.sharedAuditFacade.enableAudit$;
    this.auditScreen$ = this.sharedAuditFacade.auditScreen$;
    this.auditScreens$ = this.sharedAuditFacade.auditScreens$;
    this.selectedAuditScreen$ = this.sharedAuditFacade.selectedAuditScreen$;

    /* eslint-disable no-console */
    if (this.swUpdate.isEnabled) {
      const updatesAvailable = this.swUpdate.versionUpdates.pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
        map(evt => ({
          type: 'UPDATE_AVAILABLE',
          current: evt.currentVersion,
          available: evt.latestVersion,
          event: evt
        }))
      );

      updatesAvailable.subscribe(ev => {
        console.log('Update available: ', ev.event);
        window.location.reload();
      });

      this.swUpdate.activateUpdate().then(ev => {
        console.log('Update activated: ', ev);
      });
    } else {
      console.log('SW not enabled');
    } /* eslint-enable no-console */

    window.refreshToken = this.refreshTokenService.refreshToken;

    // Skip OIDC checkAuth for external grant routes to prevent auto-redirect
    // External grant routes handle their own OAuth callbacks from third-party services
    const currentPath = window.location.pathname;
    if (!currentPath.startsWith('/profile/external-grant')) {
      this.oidcSecurityService
        .checkAuth()
        .pipe(untilViewDestroyed(this))
        .subscribe(data => {
          if (data) {
            if (data.isAuthenticated) {
              this.appUserInfoFacade.userAuthd$ = this.appUserInfoFacade.isAuthorized();
              this.initCsat();
            }
            if (data.userData && data.userData.Policy) {
              const policies = data.userData.Policy as PolicyType[];
              !policies || policies.length > 0
                ? this.permissionsService.policies.next(policies)
                : this.permissionsService.policies.next([data.userData.Policy]);
            }

            if (data.userData) {
              const { sub, [APPIN_CUSTOMER_ID_KEY]: customerId } = data.userData;
              if (sub) {
                this.appMonitoringService.setAuthenticatedUserContext(sub, customerId);
                this.userConfigurationFacade.init();
              }
            }

            defineUser(data.userData);
          }
        });
    }
  }

  // auth
  logout() {
    this.analyticsService.clickButton('Logout button')
    this.oidcSecurityService.logoff().subscribe();
    this.appMonitoringService.clearAuthenticatedUserContext();
  }

  ngAfterViewInit() {
    this.gaTrackingService.initAnalytics();
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.resizeListener);
  }

  /* public */

  toggleSideMenu() {
    this.analyticsService.clickButton('Menu button')
    this.navService.setShowSideMenu(!this.showSiteMenu);
  }
  onSiteMenuClose() {
    this.navService.setShowSideMenu(false);
  }

  showAudit(auditScreen: AuditScreen) {
    this.auditFacade.selectAuditScreen(auditScreen);
    this.auditFacade.showAuditDrawer();
  }

  /* private */
  private init() {
    // routing event listener
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.router.events.pipe(untilViewDestroyed(this)).subscribe((event: any) => {
      this.routeInterceptor(event);
    });

    this.createShowSideMenuSubscription();
    this.gaTrackingService.insertAnalyticsScriptTags(this.document, this.renderer);

    if (
      window.location.href.indexOf('/profile/external-grant/') > 0 &&
      window.location.href.indexOf('?code=') > 0
    ) {
      sessionStorage.setItem(SessionStorageKeys.EXT_GRANT_URL, window.location.href);
    }
  }

  private routeInterceptor(event: RouterEvent): void {
    switch (event.constructor) {
      case NavigationStart:
        this.loaderService.showGlobalLoader(true);
        this.routeState = 'inactive';
        break;
      case GuardsCheckStart:
        this.routeState = 'active';
        break;
      case GuardsCheckEnd:
        this.routeState = 'inactive';
        break;
      case NavigationCancel:
      case NavigationEnd:
      case NavigationError:
        this.loaderService.hideGlobalLoader();
        this.navService.setShowSideMenu(false);
        this.routeState = 'active';
        break;
    }
  }

  // ui
  private createShowSideMenuSubscription() {
    this.navService.showSideMenu$
      .pipe(untilViewDestroyed(this))
      .subscribe(show => (this.showSiteMenu = show));
  }

  private initCsat() {
    this.csatFacade.init();
    this.csatFacade.lastAnswerDate$
      .pipe(untilViewDestroyed(this))
      .subscribe(date => {
        if (date) {
          const startDate = new Date(date);
          const endDate = new Date();
          const diff = endDate.getTime() - startDate.getTime();
          const days = Math.floor(diff / (1000 * 60 * 60 * 24));

          if (days >= 90) {
            this.csatModalService.setOpenModal(true);
          }
        } else if (date === undefined) {
          this.csatModalService.setOpenModal(true);
        }
      });
  }
}
